/**
 * ProducerProductDetail – public product detail page.
 * Route: /producer/:slugOrId/shop/:productSlug
 * Farmly tone: calm, place-first, not e-commerce.
 */
import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Store, MessageCircle, Bell } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Seo } from '@/components/common';
import { useProducer } from '@/hooks/useProducer';
import { usePublicProduct } from '@/hooks/usePublicProduct';
import { useProductCoverImages } from '@/hooks/useProductCoverImages';
import { PickupOrderDialog } from '@/components/farmshop/PickupOrderDialog';
import { ProductInterestDialog } from '@/components/farmshop/ProductInterestDialog';
import { createProductJsonLd, createBreadcrumbJsonLd } from '@/lib/seo';
import { useTrackOnce } from '@/lib/analytics/useTrackOnce';
import { stripHtmlToText } from '@/lib/text';
import { producerPublicPath, producerShopPath } from '@/lib/paths';
import { useTranslate } from '@/i18n';

function formatPrice(ore: number, currency: string = 'SEK'): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(ore / 100);
}

export default function ProducerProductDetail() {
  const t = useTranslate();
  const { slugOrId, productSlug } = useParams<{ slugOrId: string; productSlug: string }>();
  const { producer, loading: producerLoading } = useProducer(slugOrId || '');
  const { product, loading: productLoading } = usePublicProduct(producer?.id, productSlug);
  const productIds = product ? [product.id] : [];
  const { data: coverImages = {} } = useProductCoverImages(productIds, 640);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [interestOpen, setInterestOpen] = useState(false);

  const isLoading = producerLoading || productLoading;
  const coverUrl = product ? coverImages[product.id] : null;

  const baseUrl = window.location.origin;
  const producerPathKey = producer?.slug || slugOrId || '';
  const producerUrl = producerPublicPath(producerPathKey);
  const shopUrl = producerShopPath(producerPathKey);
  const productUrl = product
    ? `${baseUrl}${producerUrl}/shop/${(product as any).slug || product.id}`
    : undefined;

  const areaLabel = producer?.city || producer?.county || '';

  // SEO
  const seoTitle = useMemo(() => {
    if (!product || !producer) return 'Produkt';
    const area = areaLabel ? `, ${areaLabel}` : '';
    return `${product.title} – ${producer.name}${area}`;
  }, [product, producer, areaLabel]);

  const seoDescription = useMemo(() => {
    if (!product || !producer) return '';
    const desc = product.description ? stripHtmlToText(product.description) : '';
    const intro = desc ? `${desc.slice(0, 100)}. ` : '';
    return `${intro}Från ${producer.name}${areaLabel ? ` i ${areaLabel}` : ''}. Finns i gårdsbutiken.`.slice(0, 160);
  }, [product, producer, areaLabel]);

  const jsonLd = useMemo(() => {
    if (!product || !productUrl) return undefined;
    return [
      createProductJsonLd({
        name: product.title,
        description: product.description ? stripHtmlToText(product.description) : undefined,
        images: coverUrl ? [coverUrl] : [],
        price: product.price_ore / 100,
        priceCurrency: product.currency || 'SEK',
        availability: 'PreOrder',
        url: productUrl,
        brand: producer?.name,
      }),
      createBreadcrumbJsonLd([
        { name: 'Farmly', url: baseUrl },
        { name: producer?.name || '', url: `${baseUrl}${producerUrl}` },
        { name: 'Gårdsbutik', url: `${baseUrl}${shopUrl}` },
        { name: product.title, url: productUrl },
      ]),
    ];
  }, [product, producer, coverUrl, productUrl, baseUrl, producerUrl, shopUrl]);

  // Track view once per product
  useTrackOnce('shop_product_viewed', {
    product_id: product?.id ?? '',
    producer_id: producer?.id ?? '',
    source_surface: 'product_page',
  }, product?.id ?? null);

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <main className="flex-1 container py-8 max-w-3xl">
          <Skeleton className="h-5 w-40 mb-6" />
          <Skeleton className="aspect-[4/3] rounded-xl mb-6" />
          <Skeleton className="h-8 w-3/4 mb-2" />
          <Skeleton className="h-5 w-1/2 mb-6" />
          <Skeleton className="h-20 w-full" />
        </main>
        <SiteFooter />
      </div>
    );
  }

  // 404
  if (!product || !producer) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Seo title="Produkten hittades inte" noIndex />
        <SiteHeader />
        <main className="flex-1 container py-16 text-center">
          <h1 className="font-display text-3xl mb-4 text-foreground">Produkten hittades inte</h1>
          <p className="text-muted-foreground mb-8">Den här produkten verkar inte finnas längre.</p>
          <Button asChild variant="outline">
            <Link to={shopUrl}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till gårdsbutiken
            </Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const productForDialog = {
    id: product.id,
    title: product.title,
    price: product.price_ore / 100,
    currencyCode: product.currency || 'SEK',
    imageUrl: coverUrl,
    pickupInstructions: product.pickup_instructions ?? null,
    stockLimit: (product as any).stock_limit ?? null,
    remaining: null as number | null, // fetched lazily in dialog
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={productUrl}
        ogType="product"
        image={coverUrl}
        imageAlt={product.title}
        jsonLd={jsonLd}
      />

      <SiteHeader />

      <main className="flex-1">
        <div className="container py-8 max-w-3xl" data-testid="producer-product-detail-page">
          <Link
            to={shopUrl}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Till gårdsbutiken
          </Link>

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 flex-wrap">
            <Link to={producerUrl} className="hover:text-foreground transition-colors">
              {producer.name}
            </Link>
            <span>/</span>
            <Link to={shopUrl} className="hover:text-foreground transition-colors">
              Gårdsbutik
            </Link>
            <span>/</span>
            <span className="text-foreground">{product.title}</span>
          </nav>

          {/* Hero image */}
          {coverUrl ? (
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted mb-6">
              <img
                src={coverUrl}
                alt={product.title}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="aspect-[4/3] rounded-xl bg-muted flex items-center justify-center mb-6">
              <Store className="h-12 w-12 text-muted-foreground/30" />
            </div>
          )}

          {/* Title + origin */}
          <h1 className="font-display text-2xl md:text-3xl text-foreground mb-1">
            {product.title}
          </h1>
          <p className="text-muted-foreground mb-6 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />
            Från{' '}
            <Link to={producerUrl} className="underline underline-offset-2 hover:text-foreground transition-colors">
              {producer.name}
            </Link>
            {areaLabel && <span>i {areaLabel}</span>}
          </p>

          <Separator className="mb-6" />

          {/* Description */}
          {product.description && (
            <div className="prose prose-sm max-w-none text-foreground/90 mb-6">
              <div dangerouslySetInnerHTML={{ __html: product.description }} />
            </div>
          )}

          {/* Price + CTA */}
          <div className="rounded-xl border border-border bg-card p-5 mb-6">
            {product.status === 'upcoming' ? (
              <>
                <p className="text-xs text-muted-foreground/70 mb-2">{t("status.shop.comingSoon")}</p>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-2xl font-semibold text-foreground">
                      {formatPrice(product.price_ore, product.currency)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Lämna din e-post så meddelar vi dig när produkten finns
                    </p>
                  </div>
                  <Button variant="secondary" className="gap-2" onClick={() => setInterestOpen(true)} data-testid="producer-product-detail-request-cta">
                    <Bell className="h-4 w-4" />
                    {t("shop.cta.notifyMe")}
                  </Button>
                </div>
              </>
            ) : product.status === 'sold_out' ? (
              <>
                <p className="text-xs text-muted-foreground/70 mb-2">{t("status.shop.soldOut")}</p>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-2xl font-semibold text-foreground">
                      {formatPrice(product.price_ore, product.currency)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Produkten är tillfälligt slut – anmäl intresse så hör vi av oss
                    </p>
                  </div>
                  <Button variant="secondary" className="gap-2" onClick={() => setInterestOpen(true)} data-testid="producer-product-detail-request-cta">
                    <Bell className="h-4 w-4" />
                    Meddela mig
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground/70 mb-2">Finns i gårdsbutiken</p>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-2xl font-semibold text-foreground">
                      {formatPrice(product.price_ore, product.currency)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {t("shop.label.preorderForPickup")}
                    </p>
                  </div>
                  <Button asChild className="gap-2" data-testid="producer-product-detail-request-cta">
                    <Link to={`${shopUrl}/${(product as any).slug || product.id}/bestall`}>
                      <MessageCircle className="h-4 w-4" />
                      {t("shop.cta.continueOrder")}
                    </Link>
                  </Button>
                </div>
              </>
            )}

            {product.pickup_instructions && (
              <p className="text-sm text-muted-foreground mt-3 pt-3 border-t border-border">
                {product.pickup_instructions}
              </p>
            )}
          </div>

          {/* Producer block */}
          <div className="rounded-xl border border-border/50 bg-muted/30 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Store className="h-4 w-4 text-primary" />
              </div>
              <div>
                <Link
                  to={producerUrl}
                  className="font-medium text-foreground hover:underline underline-offset-2"
                >
                  {producer.name}
                </Link>
                {areaLabel && (
                  <p className="text-xs text-muted-foreground">{areaLabel}</p>
                )}
              </div>
            </div>
            {producer.short_description && (
              <p className="text-sm text-muted-foreground">
                {producer.short_description}
              </p>
            )}
          </div>
        </div>
      </main>

      <SiteFooter />

      {/* Pickup order dialog */}
      <PickupOrderDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        product={productForDialog}
        producerId={producer.id}
        producerName={producer.name}
        pickupInstructions={producer.pickup_instructions}
        sourceSurface="product_page"
      />

      {/* Interest dialog for upcoming / sold_out */}
      {product && producer && (
        <ProductInterestDialog
          open={interestOpen}
          onOpenChange={setInterestOpen}
          productTitle={product.title}
          productId={product.id}
          producerId={producer.id}
        />
      )}
    </div>
  );
}
